import { FC } from "react";

import { Stack } from "@mui/material";
import { FormCategory, ShareFormButtonPopupImageUrl, Locales } from "@simplyk/common";

import { CloseDialogButton } from "./CloseDialogButton";
import { EmbedInstructionsIllustration } from "./EmbedInstructionsIllustration";
import { ShareFormTabLayout } from "./ShareFormTabLayout";
import { ShareFormTabTitle } from "./ShareFormTabTitle";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { EMBEDDED_FORM_SCRIPT } from "@/common/constants/EmbedForm";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TextFieldWithCopy } from "@/components/TextFieldWithCopy/TextFieldWithCopy";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { ChevronLeft, ExternalLink } from "@/icons/outlined";

export type ButtonPopupInstructionsTabProps = {
  formCategory: FormCategory;
  onClose?: () => void;
  onBackClick?: () => void;
  guideLinks: Record<Locales, string>;
  embedCode: string;
};

export const ButtonPopupInstructionsTab: FC<ButtonPopupInstructionsTabProps> = ({
  embedCode,
  formCategory,
  onClose,
  onBackClick,
  guideLinks,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { isSmallScreen } = useMediaQuery();

  return (
    <ShareFormTabLayout
      slots={{
        closeButton: <CloseDialogButton onClick={onClose} />,
        header: <ShareFormTabTitle>{t("common", "shareFormModal.embed.choiceLinkedButton.title")}</ShareFormTabTitle>,
        content: (
          <Stack gap={3}>
            <EmbedInstructionsIllustration
              src={ShareFormButtonPopupImageUrl}
              illustrationWidth={553}
              scale={isSmallScreen ? 0.6 : 1}
              captions={[
                t("common", "shareFormModal.embed.illustration.firstCode"),
                t("common", "shareFormModal.embed.illustration.secondCode"),
                t("common", "shareFormModal.embed.illustration.adjustSizing"),
              ]}
            />
            <DashboardDivider />
            <Stack gap={1}>
              <DashboardTypography variant="subtitle2">
                {t("common", "shareFormModal.embed.choice.ButtonPopup.label1")}
              </DashboardTypography>
              <TextFieldWithCopy
                button
                value={embedCode}
                size="medium"
                buttonCopyProps={{ vibe: "brand", variant: "filled", size: "medium" }}
                copyAmplitudeEvent={AmplitudeEvents.ShareFormModalClickCopyEmbed}
                copyAmplitudePayload={{
                  form_embed_type: "ButtonPopup",
                  property: "button-property",
                  form_type: formCategory,
                }}
              />
            </Stack>
            <Stack gap={1}>
              <DashboardTypography variant="subtitle2">
                {t("common", "shareFormModal.embed.choice.ButtonPopup.label2")}
              </DashboardTypography>
              <TextFieldWithCopy
                button
                value={EMBEDDED_FORM_SCRIPT}
                size="medium"
                buttonCopyProps={{ vibe: "brand", variant: "filled", size: "medium" }}
                copyAmplitudeEvent={AmplitudeEvents.ShareFormModalClickCopyEmbed}
                copyAmplitudePayload={{
                  form_embed_type: "ButtonPopup",
                  property: "button-script",
                  form_type: formCategory,
                }}
              />
            </Stack>
            <Stack gap={1} sx={{ alignSelf: "center" }}>
              <DashboardTypography variant="subtitle2">
                {t("common", "shareFormModal.embed.choice.extraHelp")}
              </DashboardTypography>
              <DashboardButton
                vibe="brand"
                variant="outlined"
                size="medium"
                endIcon={<ExternalLink />}
                href={guideLinks[locale]}
                target="_blank"
              >
                {t("common", "shareFormModal.embed.choice.visitOurGuide")}
              </DashboardButton>
            </Stack>
          </Stack>
        ),
        footer: (
          <Stack direction="row" alignItems="center" justifyContent="flex-start">
            <DashboardButton
              vibe="neutral"
              variant="outlined"
              size="large"
              startIcon={<ChevronLeft />}
              onClick={onBackClick}
            >
              {t("common", "back")}
            </DashboardButton>
          </Stack>
        ),
      }}
    />
  );
};
